import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from "@mui/material";
import {translate} from "../../translate/translate";
import * as React from "react";
import {useEffect, useRef} from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Select from "@mui/material/Select";
import {
    ForecastProcessed,
    PredictionTechnique,
    PredictionTechniqueList,
    RepetitionFrequency,
    RepetitionFrequencyList,
    Replenishment,
    Result,
    ServiceLevel,
    StatusForecastProcessed,
    Type
} from "../../models/basic/replenishment";
import {SelectItem} from "../../models/basic/select-item";
import Button from "@mui/material/Button";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DatePicker, LocalizationProvider, TimePicker} from "@mui/x-date-pickers";
import {enUS, ptBR} from "date-fns/locale";
import {AbstractService} from "../../services/abstract-service";
import {URLS} from "../../services/app-urls";
import {ErrorComponent} from "../../components/error/error.component";
import {toast} from "react-toastify";
import {format, parseISO} from 'date-fns';
import ReplenishmentListComponent from "./replenishment-list.component";
import dayjs from "dayjs";
import {InExecutionIconComponent} from "../../components/in-execution-icon/in-execution-icon.component";
import {trackPromise} from "react-promise-tracker";
import {ReplenishmentService} from "../../services/replenishment-service";
import {formatDateToDDMMYYYY} from "../../utils/format-date";
import {Profile} from "../../models/account/user";
import {useAuth} from "../../contexts/auth-context";

const statusColorMap: Record<number, string> = {
    1: 'orange',
    2: 'green',
    [-1]: 'blue',
};

const ReplenishmentComponent = () => {

    const { user } = useAuth();
    const hasPermission = user?.perfil === Profile.ADMIN || user?.perfil === Profile.OPERACIONAL;
    const service = new AbstractService<Replenishment>(URLS.REPLENISHMENT_SETTING);
    const serviceReplenishmentParts = new ReplenishmentService();
    const [replenishment, setReplenishment] = React.useState<Replenishment>(new Replenishment());
    const [savedReplenishment, setSavedReplenishment] = React.useState<Replenishment | null>(null);

    const locale = translate.language == "pt" ? ptBR : enUS;

    const [lastUpdated, setLastUpdated] = React.useState(dayjs());
    const [expanded, setExpanded] = React.useState<boolean>(false);

    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const [forecastProcessed, setForecastProcessed] = React.useState<ForecastProcessed>({
        status: StatusForecastProcessed.UNAVAILABLE,
        processing_end_date: '',
    });

    const [configurationSaved, setConfigurationSaved] = React.useState(false);

    useEffect(() => {
        isProcessing();
        loadConfiguration();
        return () => stopPollingFn();
    }, []);

    const isProcessing = () => {
        serviceReplenishmentParts.forecast_processed().then(response => {
            setForecastProcessed(response.data);
            const executing = response.data.status === 1;
            if (executing) {
                processing();
            }
        });
    }

    const processing = (restartIndex = false) => {
        if (replenishment.type === Type.AUTOMATIC) return;
        stopPollingFn();
        setForecastProcessed(p => ({
            ...p, status: StatusForecastProcessed.PROCESSING
        }));
        pollingRef.current = setInterval(() => {
            trackPromise(
                serviceReplenishmentParts.forecast_processed().then(response => {
                    setForecastProcessed(response.data);
                    const noneProcessing = response.data.status === 2;
                    if (noneProcessing) {
                        stopPollingFn();
                        // setInExecution(false);
                    }
                }).catch((error: any) => {
                    ErrorComponent(error);
                })
            )
        }, 10000);
    };

    const stopPollingFn = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    const loadConfiguration = () => {
        service.readAll().then((response) => {
            if (response.data) {
                const obj = {
                    ...response.data[0],
                    date: response.data[0].date ? parseISO(response.data[0].date.toString()) : undefined,
                    time: response.data[0].time ? new Date(`1970-01-01T${response.data[0].time}`) : undefined,
                }
                setSavedReplenishment(obj || new Replenishment());
                setReplenishment(obj || new Replenishment());
                if (obj.type === Type.MANUAL) {
                    setConfigurationSaved(true);
                }
            } else {
                setReplenishment(new Replenishment());
                setSavedReplenishment(new Replenishment());
            }
        }).catch((error: any) => {
            ErrorComponent(error);
        })
    }

    const save = () => {
        setConfigurationSaved(false);
        const obj = {
            ...replenishment,
            date: replenishment.date ? format(replenishment.date, 'yyyy-MM-dd') : undefined,
            time: replenishment.time ? format(replenishment.time, 'HH:mm') : undefined,
        }
        service.patch(replenishment.id, obj).then((response) => {
            toast.success(translate.t("successfully_updated"));
            setConfigurationSaved(replenishment.type === Type.MANUAL);
            setSavedReplenishment(replenishment);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const legend = PredictionTechniqueList.find(it => it.value === replenishment.prediction_technique)?.legend || "";

    const canSave = () => {
        if (replenishment.type === Type.AUTOMATIC) {
            if (replenishment.repetition_frequency == 1) {
                return replenishment.date &&
                    replenishment.repetition_frequency &&
                    replenishment.prediction_technique &&
                    replenishment.month_forecast > 0 &&
                    validDate();
            }
            return replenishment.date &&
                replenishment.repetition_frequency &&
                replenishment.prediction_technique &&
                replenishment.month_forecast > 0;
        }
        return replenishment.month_forecast > 0;
    }

    const validDate = (): boolean => {
        if (!replenishment.date) return false;

        const minDate = new Date();
        minDate.setHours(0, 0, 0, 0);

        return replenishment.date >= minDate;
    };

  return (
      <section>
          <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
              <Typography variant={"h5"}>
                  {translate.t("replenishment")}
              </Typography>
          </div>

          <div className="floating-button" style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span
                    className="text"
                    style={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: statusColorMap[forecastProcessed?.status ?? -1],
                        borderRadius: '50%',
                        display: 'inline-block',
                        marginRight: '5px'
                    }}
                />
              {forecastProcessed?.status !== -1 && (
                  <p className="text">
                      {translate.t("last_replenishment")}:&ensp;
                      {forecastProcessed?.processing_end_date
                          ? formatDateToDDMMYYYY(forecastProcessed.processing_end_date, true)
                          : "-"
                      }
                  </p>
              )}

              {forecastProcessed && forecastProcessed?.status === -1 && (
                  <p className="text">
                      {translate.t("no_replenishment_info")}
                  </p>
              )}
              {forecastProcessed.status === StatusForecastProcessed.PROCESSING && (<InExecutionIconComponent/>)}
          </div>

          {hasPermission && (
              <Accordion sx={{'&.Mui-expanded': {margin: 0}}}
                      expanded={expanded}
                      disabled={forecastProcessed.status === StatusForecastProcessed.PROCESSING}
                      onChange={(_, isExpanded) => setExpanded(isExpanded)}
              >
                  <AccordionSummary
                      expandIcon={<ExpandMoreIcon/>}
                      aria-controls="panel1-content"
                      id="panel1-header"
                  >
                      <Typography variant="h5">Configurações</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                      <Box sx={{display: 'flex', gap: 4}}>
                          <Box sx={{display: 'flex', gap: 4, flexDirection: 'column', flexGrow: 1, minWidth: '45%'}}>
                              <FormControl>
                                  <FormLabel id="demo-row-radio-buttons-group-label">Executar replenishment</FormLabel>
                                  <RadioGroup
                                      row
                                      aria-labelledby="demo-row-radio-buttons-group-label"
                                      name="row-radio-buttons-group"
                                      value={replenishment.type}
                                      onChange={(event) => {
                                          const value = event.target.value;
                                          setReplenishment({
                                              ...replenishment,
                                              type: value as Type
                                          });
                                      }}
                                  >
                                      <FormControlLabel value={Type.MANUAL} control={<Radio/>} label="Manual"/>
                                      <FormControlLabel value={Type.AUTOMATIC} control={<Radio/>} label="Automático"/>
                                  </RadioGroup>
                              </FormControl>

                              {replenishment.type === Type.AUTOMATIC && (
                                  <Box sx={{display: 'flex', gap: 2}}>
                                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                                          <DatePicker
                                              label={translate.t("day")}
                                              value={replenishment.date}
                                              onChange={(date) => {
                                                  if (date instanceof Date && !isNaN(date.getTime())) {
                                                      setReplenishment({
                                                          ...replenishment,
                                                          date: date
                                                      });
                                                  }
                                              }}
                                              minDate={replenishment.repetition_frequency == 1 ? new Date() : undefined}
                                              inputRef={(props: any) => (
                                                  <TextField
                                                      {...props}
                                                      required={true}
                                                  />
                                              )}
                                              slotProps={{
                                                  textField: {
                                                      fullWidth: true,
                                                      required: true,
                                                  }
                                              }}
                                          />
                                      </LocalizationProvider>

                                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                                          <TimePicker
                                              label={translate.t("hour")}
                                              value={replenishment.time}
                                              onChange={(time) => {
                                                  if (time instanceof Date && !isNaN(time.getTime())) {
                                                      setReplenishment({
                                                          ...replenishment,
                                                          time: time
                                                      });
                                                  }
                                              }}
                                              slotProps={{
                                                  textField: {
                                                      fullWidth: true,
                                                      required: false
                                                  }
                                              }}
                                          />
                                      </LocalizationProvider>

                                      <FormControl fullWidth>
                                          <InputLabel required>
                                              Repetir a cada
                                          </InputLabel>
                                          <Select
                                              name="repetition_frequency"
                                              label="Repetir a cada"
                                              variant={"outlined"}
                                              fullWidth
                                              autoFocus={false}
                                              value={replenishment.repetition_frequency}
                                              onChange={(event) => {
                                                  const value = event.target.value;
                                                  setReplenishment({
                                                      ...replenishment,
                                                      repetition_frequency: value as RepetitionFrequency
                                                  });
                                              }}
                                          >
                                              {RepetitionFrequencyList?.map((item: SelectItem) => (
                                                  <MenuItem key={item.value} value={item.value}>
                                                      {item.label}
                                                  </MenuItem>
                                              ))}
                                          </Select>
                                      </FormControl>
                                  </Box>
                              )}

                              <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                  <FormLabel id="demo-row-radio-buttons-group-label">Técnicas de Predição</FormLabel>
                                  <FormControl fullWidth>
                                      <InputLabel>
                                          Selecione
                                      </InputLabel>
                                      <Select
                                          name="prediction_technique"
                                          label="Selecione"
                                          variant={"outlined"}
                                          fullWidth
                                          autoFocus={false}
                                          value={replenishment.prediction_technique}
                                          onChange={(event) => {
                                              const value = event.target.value;
                                              setReplenishment({
                                                  ...replenishment,
                                                  prediction_technique: value as PredictionTechnique
                                              });
                                          }}
                                      >
                                          {PredictionTechniqueList?.map((item: SelectItem) => (
                                              <MenuItem key={item.value} value={item.value}>
                                                  {item.label}
                                              </MenuItem>
                                          ))}
                                      </Select>
                                      {!!replenishment.prediction_technique && (
                                          <Alert severity="info" icon={false} sx={{marginTop: "16px"}}>
                                              <span dangerouslySetInnerHTML={{__html: legend}}/>
                                          </Alert>
                                      )}
                                  </FormControl>
                              </Box>
                          </Box>

                          <Box sx={{display: 'flex', gap: 4, flexDirection: 'column', flexGrow: 1}}>
                              <FormControl>
                                  <FormLabel id="demo-row-radio-buttons-group-label">Visualizar resultado por</FormLabel>
                                  <RadioGroup
                                      row
                                      aria-labelledby="demo-row-radio-buttons-group-label"
                                      name="row-radio-buttons-group"
                                      value={replenishment.result}
                                      onChange={(event) => {
                                          const value = event.target.value;
                                          setReplenishment({
                                              ...replenishment,
                                              result: value as Replenishment['result']
                                          });
                                      }}
                                  >
                                      <FormControlLabel value={Result.LIFE_CYCLE} control={<Radio/>} label="Ciclo de Vida"/>
                                      <FormControlLabel value={Result.USEFUL_LIFE} control={<Radio/>} label="Vida útil"/>
                                  </RadioGroup>
                              </FormControl>
                              <FormControl>
                                  <FormLabel id="demo-row-radio-buttons-group-label">Nível de serviço</FormLabel>
                                  <RadioGroup
                                      row
                                      aria-labelledby="demo-row-radio-buttons-group-label"
                                      name="row-radio-buttons-group"
                                      value={replenishment.service_level}
                                      onChange={(event) => {
                                          const value = event.target.value;
                                          setReplenishment({
                                              ...replenishment,
                                              service_level: value as Replenishment['service_level']
                                          });
                                      }}
                                  >
                                      <FormControlLabel value={ServiceLevel.P85} control={<Radio/>} label="85%"/>
                                      <FormControlLabel value={ServiceLevel.P90} control={<Radio/>} label="90%"/>
                                      <FormControlLabel value={ServiceLevel.P95} control={<Radio/>} label="95%"/>
                                      <FormControlLabel value={ServiceLevel.P995} control={<Radio/>} label="99,5%"/>
                                  </RadioGroup>
                              </FormControl>
                          </Box>
                          <Box sx={{display: 'flex', gap: 4, flexDirection: 'column', flexGrow: 1}}>
                              <FormControl sx={{maxWidth: '200px'}}>
                                  <FormLabel id="demo-row-radio-buttons-group-label">Previsão para</FormLabel>
                                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                      <TextField
                                          name="month_forecast"
                                          variant="outlined"
                                          fullWidth
                                          type="number"
                                          value={replenishment.month_forecast}
                                          error={replenishment.month_forecast <= 0 || replenishment.month_forecast > 12}
                                          onChange={(event) => {
                                              const parsed = parseInt(event.target.value, 10) || 0;
                                              if (parsed < 0 || isNaN(parsed) || parsed > 12) return;
                                              setReplenishment({
                                                  ...replenishment,
                                                  month_forecast: parsed < 0 ? 0 : parsed
                                              });
                                          }}
                                          onBlur={(event) => {
                                              const parsed = parseInt(event.target.value, 10) || 0;
                                              setReplenishment({
                                                  ...replenishment,
                                                  month_forecast: parsed < 0 ? 0 : parsed
                                              });
                                          }}
                                          inputProps={{
                                              sx: {
                                                  maxHeight: '0px !important',
                                                  height: '0px !important',
                                              },
                                          }}
                                      />
                                      <Typography variant="body2" color="textSecondary">
                                          mes(es)
                                      </Typography>
                                  </Box>
                              </FormControl>
                          </Box>
                      </Box>
                      <Box sx={{display: 'flex', gap: 4, justifyContent: 'flex-end', marginTop: 2}}>
                          <Button
                              color="secondary"
                              variant="outlined"
                              disabled={!canSave()}
                              onClick={() => {
                                  save();
                              }}>SALVAR CONFIGURAÇÕES
                          </Button>
                      </Box>
                  </AccordionDetails>
            </Accordion>
          )}

          <ReplenishmentListComponent
              inExecution={() => {
                  setExpanded(false);
                  setForecastProcessed(p => ({
                      ...p, status: StatusForecastProcessed.PROCESSING
                  }));
                  processing();
              }}
              disabled={(replenishment.type == Type.AUTOMATIC && !configurationSaved) || (replenishment.type == Type.MANUAL && !configurationSaved)}
              processing={forecastProcessed.status === StatusForecastProcessed.PROCESSING}
              configuration={savedReplenishment}
              hasPermission={hasPermission}
          />

      </section>
  );
}

export default ReplenishmentComponent;
